import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { applySwatchBackground, isOnMainProductPage, requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #syncAbortController;

  /** @type {AbortController | undefined} */
  #changeAbortController;

  connectedCallback() {
    super.connectedCallback();

    this.#changeAbortController = new AbortController();
    const { signal } = this.#changeAbortController;
    this.addEventListener('change', this.variantChanged.bind(this), { signal });

    if (this.#shouldBroadcastGlobally()) {
      this.#syncAbortController = new AbortController();
      document.addEventListener(ThemeEvents.variantUpdate, this.#handleGlobalVariantUpdate, {
        signal: this.#syncAbortController.signal,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#changeAbortController?.abort();
    this.#changeAbortController = undefined;
    this.#syncAbortController?.abort();
    this.#syncAbortController = undefined;
  }

  /**
   * Check if this variant picker should broadcast/listen for global events.
   * Only product page variant pickers (not in product cards or quick add dialogs) should sync.
   * @returns {boolean}
   */
  #shouldBroadcastGlobally() {
    return this.dataset.templateProductMatch === 'true' && isOnMainProductPage(this);
  }

  /**
   * Handles global variant update events from other variant pickers.
   * Syncs selections when the event is for the same product but from a different picker.
   * @param {VariantUpdateEvent} event
   */
  #handleGlobalVariantUpdate = (event) => {
    const { productId, sourceElement, html } = event.detail?.data ?? {};

    if (sourceElement === this) return;

    if (productId !== this.dataset.productId) return;

    const variant = event.detail?.resource;
    if (!variant) return;

    if (html) {
      try {
        this.updateVariantPicker(html);
      } catch (error) {
        console.warn('Failed to morph variant picker from global update', error);
      }
    }

    this.#syncSelections(variant);
  };

  /**
   * Syncs the variant picker selections to match the given variant.
   * @param {any} variant - The variant object with option1, option2, option3 properties.
   */
  #syncSelections(variant) {
    const selects = this.querySelectorAll('select');
    const fieldsets = this.querySelectorAll('fieldset');
    const customDialogs = this.querySelectorAll('variant-select-dialog');

    // Sync custom dialog elements (custom dropdown style)
    for (const dialog of customDialogs) {
      if (!(dialog instanceof HTMLElement)) continue;

      const optionPosition = dialog.dataset.optionPosition;
      const optionIndex = optionPosition ? Number.parseInt(optionPosition, 10) : null;
      if (!optionIndex || optionIndex < 1 || optionIndex > 3) continue;

      const optionValue = variant[`option${optionIndex}`];
      if (!optionValue) continue;

      // @ts-expect-error - syncSelection is a custom method on VariantSelectDialog
      if (typeof dialog.syncSelection === 'function') {
        dialog.syncSelection(optionValue);
      }

      this.#updateSelectedValueDisplay(optionIndex, optionValue);
    }

    // Sync select elements (native dropdown style - may be hidden selects inside custom dialogs)
    for (const select of selects) {
      const optionPosition = select.dataset.optionPosition;
      const optionIndex = optionPosition ? Number.parseInt(optionPosition, 10) : null;
      if (!optionIndex || optionIndex < 1 || optionIndex > 3) continue;

      if (select.closest('variant-select-dialog')) continue;

      const optionValue = variant[`option${optionIndex}`];
      if (!optionValue) continue;

      /** @type {HTMLOptionElement | null} */
      let matchedOption = null;
      for (const opt of select.options) {
        if (opt.value === optionValue) {
          matchedOption = opt;
          break;
        }
      }
      if (!matchedOption) {
        for (const opt of select.options) {
          if (opt.textContent?.includes(optionValue)) {
            matchedOption = opt;
            break;
          }
        }
      }

      if (matchedOption && !matchedOption.selected) {
        matchedOption.selected = true;
        // Update selected attribute for proper state
        for (const opt of select.options) {
          opt.removeAttribute('selected');
        }
        matchedOption.setAttribute('selected', 'selected');
      }

      this.#updateSelectedValueDisplay(optionIndex, optionValue);

      // Update the dropdown swatch if present
      this.#updateDropdownSwatch(optionIndex, matchedOption);
    }

    let fieldsetIndex = 0;
    for (const fieldset of fieldsets) {
      fieldsetIndex += 1;
      if (fieldsetIndex > 3) continue;

      const optionValue = variant[`option${fieldsetIndex}`];
      if (!optionValue) continue;

      let input = fieldset.querySelector(`input[value="${CSS.escape(optionValue)}"]`);

      const optionLabel = input?.closest('.variant-option__button-label');
      if (optionLabel instanceof HTMLElement && optionLabel.dataset.optionOnProduct === 'false') {
        input = fieldset.querySelector(
          '.variant-option__button-label:not([data-option-on-product="false"]) input[data-option-available="true"]'
        ) ?? fieldset.querySelector('.variant-option__button-label:not([data-option-on-product="false"]) input');
      }

      if (input instanceof HTMLInputElement && !input.checked) {
        input.checked = true;
      }

      const displayValue = input instanceof HTMLInputElement ? input.value : optionValue;
      this.#updateSelectedValueDisplay(fieldsetIndex, displayValue);
    }
  }

  /** @param {number} index @param {string} value */
  #updateSelectedValueDisplay(index, value) {
    const el = this.querySelector(`[ref="selectedValue-${index}"]`);
    if (el) el.textContent = value;
  }

  /**
   * Updates the dropdown swatch element based on the selected option's data attributes.
   * @param {number} optionPosition - The option position (1, 2, or 3)
   * @param {HTMLOptionElement | null} selectedOption - The selected option element
   */
  #updateDropdownSwatch(optionPosition, selectedOption) {
    const swatchContainer = this.querySelector(`[ref="dropdownSwatch-${optionPosition}"]`);
    if (!swatchContainer || !selectedOption) return;

    /** @type {HTMLElement | null} */
    const swatchEl = swatchContainer.querySelector('.swatch');
    if (!swatchEl) return;

    const { swatchColor, swatchImage, variantImage } = selectedOption.dataset;
    applySwatchBackground(swatchEl, { swatchImage, swatchColor, variantImage });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // Immediately update dropdown swatch if this is a select change
    if (event.target instanceof HTMLSelectElement) {
      const optionPosition = event.target.dataset.optionPosition;
      if (optionPosition && selectedOption instanceof HTMLOptionElement) {
        this.#updateDropdownSwatch(Number.parseInt(optionPosition, 10), selectedOption);
      }
    }

    const productId = this.dataset.productId;
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }, { productId }));

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = this.#shouldBroadcastGlobally() && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (this.#shouldBroadcastGlobally()) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} targetInput - The target element or option value ID string.
   */
  updateSelectedOption(targetInput) {
    /** @type {Element} */
    let target;
    if (typeof targetInput === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${CSS.escape(targetInput)}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    } else {
      target = targetInput;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          const previousProductId = this.dataset.productId;
          this.updateMain(html);

          if (VariantPicker.correctHiddenSelections()) return;

          if (this.selectedOptionId) {
            const variantData = JSON.parse(textContent);
            const newVP = html.querySelector('variant-picker');
            const newProductId =
              (newVP instanceof HTMLElement ? newVP.dataset.productId : undefined) ?? this.dataset.productId ?? '';
            const newProductUrl = newVP instanceof HTMLElement ? newVP.dataset.productUrl : undefined;
            const newProduct =
              newProductId && newProductUrl && newProductId !== previousProductId
                ? { id: newProductId, url: newProductUrl }
                : undefined;

            document.dispatchEvent(
              new VariantUpdateEvent(variantData, this.selectedOptionId, {
                html: null,
                productId: newProductId,
                newProduct,
                sourceElement: this,
              })
            );
          }
        } else {
          const newProduct = this.#morphForContext(html);

          if (VariantPicker.correctHiddenSelections(this)) return;

          if (this.selectedOptionId) {
            const variantData = JSON.parse(textContent);
            const eventData = {
              html: newProduct?.didMorphModal ? null : html,
              productId: this.dataset.productId ?? '',
              newProduct: newProduct?.info,
              sourceElement: this,
            };
            this.dispatchEvent(new VariantUpdateEvent(variantData, this.selectedOptionId, eventData));
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    // Use data-block-id to find the matching variant picker in the response HTML
    // This ensures each variant picker morphs with its own settings (buttons vs dropdowns)
    const blockId = this.dataset.blockId;
    let newVariantPickerSource = null;

    if (blockId) {
      newVariantPickerSource = newHtml.querySelector(`variant-picker[data-block-id="${CSS.escape(blockId)}"]`);
    }

    // Fall back to first variant picker if no match found (for backwards compatibility)
    if (!newVariantPickerSource) {
      newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    }

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * Handles product-sticky-details elements that move themselves to <main> via connectedCallback,
   * which would corrupt the morph iteration if allowed to run mid-morph.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    const StickyDetails = /** @type {CustomElementConstructor & { moveToMain?: boolean }} */ (
      customElements.get('product-sticky-details')
    );
    if (StickyDetails) StickyDetails.moveToMain = false;

    for (const el of main.querySelectorAll(':scope > product-sticky-details')) {
      el.remove();
    }

    try {
      morph(main, newMain);
    } finally {
      if (StickyDetails) {
        StickyDetails.moveToMain = true;
        for (const el of main.querySelectorAll('product-sticky-details')) {
          const sticky = /** @type {Element & { reinitialize?: () => void }} */ (el);
          if (typeof sticky.reinitialize === 'function') sticky.reinitialize();
        }
      }
    }
  }

  /**
   * Chooses the correct morph strategy based on context.
   * In the quick-add modal with a product change: morphs the entire modal content (title, price, media, etc.).
   * Otherwise: morphs only the variant picker via updateVariantPicker.
   * Only one morph is performed per call to avoid depleting the parsed HTML Document.
   * @param {Document} html - The fetched product page document.
   * @returns {{ info: NewProduct | undefined, didMorphModal: boolean }}
   */
  #morphForContext(html) {
    const modalContent = this.closest('#quick-add-modal-content');

    const blockId = this.dataset.blockId;
    const newVP = blockId
      ? html.querySelector(`variant-picker[data-block-id="${CSS.escape(blockId)}"]`)
      : html.querySelector('variant-picker');
    const newProductId = newVP instanceof HTMLElement ? newVP.dataset.productId : undefined;
    const newProductUrl = newVP instanceof HTMLElement ? newVP.dataset.productUrl : undefined;
    const isProductChange = !!(newProductId && newProductUrl && newProductId !== this.dataset.productId);
    const info = isProductChange ? { id: newProductId, url: newProductUrl } : undefined;

    if (isProductChange && modalContent) {
      const newGridContent = html.querySelector('[data-product-grid-content]');
      if (newGridContent) {
        morph(modalContent, newGridContent);
        this.dataset.productId = newProductId;
        this.dataset.productUrl = newProductUrl;

        for (const slide of modalContent.querySelectorAll('slideshow-slide[on\\:click*="zoom"]')) {
          slide.removeAttribute('on:click');
        }
        for (const button of modalContent.querySelectorAll('.product-media-container__zoom-button')) {
          button.remove();
        }

        const dialog = modalContent.closest('dialog.quick-add-modal');
        if (dialog) {
          const existingFloating = dialog.querySelector(':scope > .buy-buttons-block');
          if (existingFloating) existingFloating.remove();

          const buyButtons = modalContent.querySelector('.buy-buttons-block');
          if (buyButtons) {
            buyButtons.classList.add('buy-buttons-block--floating');
            dialog.appendChild(buyButtons);
          }
        }

        return { info, didMorphModal: true };
      }
    }

    const updatedProduct = this.updateVariantPicker(html);
    return { info: updatedProduct, didMorphModal: false };
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * After a combined listing product switch, a non-combined option (e.g. Color)
   * may still have a value selected that doesn't exist on the new child product.
   * Scans variant pickers for such cases and auto-selects the first available
   * option, triggering a re-fetch for the correct variant.
   * @param {Element} [picker] - A specific picker to check. When omitted, scans all live pickers.
   * @returns {boolean} Whether a correction was made.
   */
  static correctHiddenSelections(picker) {
    const hiddenSelector = '.variant-option__button-label[data-option-on-product="false"]';
    const visibleSelector = '.variant-option__button-label:not([data-option-on-product="false"])';

    const pickers = picker ? [picker] : document.querySelectorAll('variant-picker');

    for (const p of pickers) {
      for (const fieldset of p.querySelectorAll('fieldset')) {
        const checked = fieldset.querySelector('input:checked');
        if (!checked || !checked.closest(hiddenSelector)) continue;

        const firstValid =
          fieldset.querySelector(`${visibleSelector} input[data-option-available="true"]`) ??
          fieldset.querySelector(`${visibleSelector} input`);

        if (firstValid instanceof HTMLInputElement) {
          firstValid.checked = true;
          firstValid.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }
    return false;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
